import { useEffect, useRef } from 'react';
import { ArrowRight, Zap, Shield, Smartphone, Users } from 'lucide-react';
import AwardsSection from './AwardsSection';

const WHATSAPP_GENERAL = 'https://wa.me/5551996777625?text=Ol%C3%A1!%20Gostaria%20de%20falar%20com%20um%20especialista%20iGreen.';

function AnimatedEntry({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    const timer = setTimeout(() => {
      el.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

export default function HeroSection() {
  return (
    <section
      id="inicio"
      className="relative flex min-h-[100svh] flex-col items-stretch overflow-hidden bg-background-950"
    >
      {/* Background glow */}
      <div className="absolute inset-0 bg-radial-green pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-radial-green-sm rounded-full pointer-events-none opacity-60" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(oklch(var(--primary-500) / 0.15) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.15) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="site-frame relative z-10 flex flex-1 flex-col items-center justify-center gap-10 pb-10 pt-32 md:pt-36 lg:flex-row lg:gap-[clamp(2rem,4vw,5rem)] lg:pb-10 lg:pt-32">
        {/* Left Content */}
        <div className="w-full max-w-3xl flex-1 text-left">
          <AnimatedEntry delay={100} className="flex justify-center md:justify-start">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
              Energia · Seguro · Telecom · Oportunidade
            </span>
          </AnimatedEntry>

          <AnimatedEntry delay={200}>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.05] tracking-tighter">
              Economize na sua conta de{' '}
              <span className="text-gradient">energia</span>
            </h1>
          </AnimatedEntry>

          <AnimatedEntry delay={350}>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-foreground-400 md:text-xl">
              A iGreen Energy conecta energia, seguro veicular, telecom e uma oportunidade
              de renda recorrente em um só ecossistema.
            </p>
          </AnimatedEntry>

          <AnimatedEntry delay={500}>
            <div className="mt-8 flex flex-col items-stretch justify-start gap-4 sm:flex-row sm:items-center">
              <a
                href={WHATSAPP_GENERAL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary w-full sm:w-auto text-base px-8 py-4 group"
              >
                Fale com um especialista
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#solucoes"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#solucoes')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-ghost w-full sm:w-auto text-base px-8 py-4"
              >
                Conhecer as soluções
              </a>
            </div>
          </AnimatedEntry>

          {/* Trust badges */}
          <AnimatedEntry delay={700} className="mt-10">
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-sm text-foreground-500 md:justify-start">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                <span>Sem obras</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                <span>Sem investimento</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                <span>Sem fidelidade</span>
              </div>
            </div>
          </AnimatedEntry>
        </div>

        {/* Right Visual */}
        <div className="relative mx-auto w-full max-w-2xl flex-1 lg:mx-0 lg:max-w-none">
          <AnimatedEntry delay={400}>
            <div className="relative rounded-3xl overflow-hidden border border-background-800/50 glow-green bg-background-900/80 backdrop-blur-xl p-6 md:p-8">
              <h3 className="font-heading text-lg md:text-xl font-bold text-white mb-5 tracking-tight">
                O que você procura na{' '}
                <span className="text-gradient">iGreen Energy</span>?
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  {
                    icon: Zap,
                    id: 'energia',
                    title: 'Reduza sua conta sem investimentos',
                    subtitle: 'Reduza sua conta sem obras',
                    color: 'bg-primary-500/20 text-primary-400',
                  },
                  {
                    icon: Shield,
                    id: 'seguro',
                    title: 'Seguro Veicular',
                    subtitle: 'Proteção com planos flexíveis',
                    color: 'bg-accent-500/20 text-accent-400',
                  },
                  {
                    icon: Smartphone,
                    id: 'telecom',
                    title: 'iGreen Telecom',
                    subtitle: 'Dados que acumulam',
                    color: 'bg-secondary-500/20 text-secondary-400',
                  },
                  {
                    icon: Users,
                    id: 'licenciado',
                    title: 'Seja um Licenciado',
                    subtitle: 'Construa renda recorrente',
                    color: 'bg-primary-500/20 text-primary-400',
                  },
                ].map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(`#${item.id}`)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="group flex items-center gap-4 px-4 py-3 rounded-xl bg-background-950/60 border border-background-800/50 hover:border-primary-500/40 hover:bg-background-950 transition-all duration-300"
                  >
                    <div
                      className={`w-14 h-14 rounded-xl ${item.color} flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110`}
                    >
                      <item.icon className="w-7 h-7" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{item.title}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-primary-400 transition-transform duration-300 group-hover:translate-x-1 shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          </AnimatedEntry>

        </div>
      </div>

      <AwardsSection />
    </section>
  );
}
